import React, { useContext } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { navigate } from '@reach/router';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { useWindowSize } from 'hooks';
import { overviewBuilder } from 'navigation/routes';
import strings from 'common/strings';
import StepDropdown from 'components/shared/StepDropdown';
import UsersDropdown from 'components/shared/UsersDropdown';
import StepButtons from 'components/shared/StepButtons';
import { testIds, generateTestId } from 'common/testIds';
import VehicleTimesTable from '../VehicleTimesTable';
import { StepItem } from 'models';
import { VehicleCardContext } from '../../VehicleCard';
import { getFullName } from 'utils/user';

interface VehicleCardUserDropDownInterface {
  currentStep?: StepItem;
}

var VehicleCardReconDetails = () => {
  const { vehicle, inExtensionCardMode, isOnboarding, position } =
    useContext(VehicleCardContext);
  const vehicleInRecon = vehicle?.vehicleCard.inRecon;
  const vehicleId = vehicle?.vehicleCard.id;
  const currentStep = vehicle?.stepItem;
  const windowSize = useWindowSize();
  const handleClickNavToRecon = (clickSource: string): Promise<void> | null => {
    if (vehicleId) {
      return navigate(`${overviewBuilder(vehicleId, true)}`);
    }
    return null;
  };

  var VehicleCardUserDropDown = (): React.ReactElement | null => {
    if (!vehicle) {
      return null;
    }
    if (inExtensionCardMode) {
      const assignedTo = currentStep?.assignedTo;
      return (
        <div>{assignedTo ? getFullName(assignedTo) : strings.EMPTY_VALUE}</div>
      );
    }
    if (currentStep) {
      return (
        <UsersDropdown
          vehicleId={vehicleId}
          currentStep={currentStep}
          loaderType="srpGradient"
        />
      );
    }
    return null;
  };

  var VehicleCardStepDropdown = ({
    currentStep,
  }: VehicleCardUserDropDownInterface): React.ReactElement | null => {
    const { vehicle } = useContext(VehicleCardContext);
    if (!vehicle) {
      return null;
    }
    if (inExtensionCardMode) {
      return <div>{currentStep?.name || strings.EMPTY_VALUE}</div>;
    }
    const vehicleCurrentStep = currentStep;
    if (
      vehicleCurrentStep !== undefined &&
      vehicleCurrentStep?.name !== undefined &&
      vehicleCurrentStep?.id !== undefined
    ) {
      const currentStepPart = {
        name: vehicleCurrentStep.name,
        id: vehicleCurrentStep.id,
      };
      return (
        <StepDropdown
          vehicleId={vehicleId}
          currentStep={currentStepPart}
          containerClassName="UsersDropdown"
          loaderType="srpGradient"
        />
      );
    }
    return null;
  };

  if (inExtensionCardMode) {
    const assignedTo = currentStep?.assignedTo;
    return (
      <>
        <Row>
          <Col>
            <div className="VehicleCard-label">
              {strings.ASSIGNEE.toUpperCase()}
            </div>
            {vehicleInRecon ? (
              <div>
                {assignedTo ? getFullName(assignedTo) : strings.EMPTY_VALUE}
              </div>
            ) : (
              strings.EMPTY_VALUE
            )}
          </Col>
          <Col>
            <div className="VehicleCard-label">
              {strings.CURRENT_STEP.toUpperCase()}
            </div>
            {vehicle && currentStep && vehicleInRecon ? (
              <div>{currentStep?.name || strings.EMPTY_VALUE}</div>
            ) : (
              strings.EMPTY_VALUE
            )}
          </Col>
        </Row>
        <Row className="VehicleCard-recon-table">
          {vehicleInRecon && <VehicleTimesTable isRecon />}
        </Row>
      </>
    );
  }

  var TabButtonsRow = () => {
    if (windowSize.width < 1350) {
      return (
        <div className="VehicleCard-step-buttons-container d-flex">
          <Col className="px-0 pr-2">
            {currentStep && (
              <StepButtons
                vehicleId={vehicleId}
                currentStep={currentStep}
                isOnboarding={isOnboarding}
                onComplete={() => true}
                small
              />
            )}
          </Col>
          <Col className="px-0 pl-2">
            <Button
              variant="outline-primary"
              block
              onClick={() =>
                handleClickNavToRecon('VehicleCard-step-buttons-container')
              }
            >
              {strings.VIEW_VEHICLE.toUpperCase()}
            </Button>
          </Col>
        </div>
      );
    }
    return (
      <div className="tab-buttons-row flex-columns align-center">
        <Button
          as="button"
          className="see-more-button"
          variant="link"
          onClick={() =>
            handleClickNavToRecon('VehicleCard-step-buttons-container')
          }
        >
          <span>{strings.VIEW_MORE}</span>
          <ChevronRightIcon />
        </Button>
        <div className="VehicleCard-step-buttons-container ml-auto">
          {currentStep && (
            <StepButtons
              vehicleId={vehicleId}
              currentStep={currentStep}
              isOnboarding={isOnboarding}
              onComplete={() => true}
              small
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Row
        className="full-height"
        data-vas-testing={generateTestId(
          testIds.INVENTORY_VEHICLE_CARD_RECON_CONTAINER,
          {
            position: position.toString(),
          }
        )}
      >
        <Col className="d-flex flex-column">
          <div className="VehicleCard-step-container">
            <div className="VehicleCard-label">
              {strings.STEP.toUpperCase()}
            </div>
            <VehicleCardStepDropdown currentStep={currentStep ?? undefined} />
          </div>
          <div className="VehicleCard-tabs-row-dropdown-item assignee-container">
            <div className="VehicleCard-label">
              {strings.ASSIGNEE.toUpperCase()}
            </div>
            <VehicleCardUserDropDown />
          </div>
          <div className="VehicleCard-times-container">
            <VehicleTimesTable isRecon position={position} />
          </div>
          <TabButtonsRow />
        </Col>
      </Row>
    </>
  );
};

export default VehicleCardReconDetails;
